import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  FileText,
  Copy,
  CheckCircle2,
  CircleDollarSign,
  Undo2,
  ScanLine,
  AlertTriangle,
  Loader2,
  X,
  FileCode2,
  ShieldCheck,
  Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";
import { FaceDialog } from "@/components/FaceDialog";

const fmtEur = (n) => {
  const v = Number(n) || 0;
  return `${v
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  if (s.length >= 10 && s[4] === "-" && s[7] === "-") {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  return s;
};

// Fecha local (en España la UTC puede ir un día por detrás de madrugada).
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyLine = () => ({
  description: "",
  units: 1,
  unit_price: 0,
  vat_rate: 0,
});

const ISSEFE_NIF = "B87237483";

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// En las facturas a ISSEFE mostramos debajo la empresa donde se impartió.
const isIssefeClient = (snapshot) => {
  const name = (snapshot?.name || "").toUpperCase();
  return name.includes("ISSE") || (snapshot?.nif || "").toUpperCase() === ISSEFE_NIF;
};

// Factura pendiente cuyo vencimiento ya ha pasado → la pintamos en rojo.
const isOverdue = (inv) =>
  !inv.payment_date && !!inv.due_date && inv.due_date < todayIso();

// BSAFE paga los días 20: el próximo es el de este mes o, pasado el 20, el del siguiente.
const nextBsafePayday = () => {
  const [y, m, d] = todayIso().split("-").map(Number);
  const date = Number(d) <= 20 ? new Date(y, m - 1, 20) : new Date(y, m, 20);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-20`;
};

// Previstas para cobrar de BSAFE ese día 20: pendientes que vencen hasta entonces (incluidas las ya vencidas).
const isBsafeExpected = (inv, payday) =>
  isIssefeClient(inv.client_snapshot) && !inv.payment_date && !!inv.due_date && inv.due_date <= payday;

async function downloadInvoicePdf(invoice) {
  try {
    const res = await adminApi.get(
      `/admin/invoicing/invoices/${invoice.id}/pdf`,
      { responseType: "blob" }
    );
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Factura_${invoice.number || invoice.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    logError("Error descargando PDF", e);
    toast.error("No se pudo descargar el PDF.");
  }
}

async function downloadFacturae(invoice) {
  try {
    const res = await adminApi.get(
      `/admin/invoicing/invoices/${invoice.id}/facturae`,
      { responseType: "blob" }
    );
    const blob = new Blob([res.data], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Facturae_${invoice.number || invoice.id.slice(0, 8)}.xsig`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success("e-Factura firmada y descargada.");
  } catch (e) {
    let detail = "No se pudo generar la e-factura.";
    try {
      const text = await e?.response?.data?.text?.();
      if (text) detail = JSON.parse(text).detail || detail;
    } catch (_) {
      detail = e?.response?.data?.detail || detail;
    }
    logError("Error generando e-factura", e);
    toast.error(detail);
  }
}

export default function InvoicesSection() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payDate, setPayDate] = useState("");
  const [paying, setPaying] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateNumber, setDuplicateNumber] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");
  const [search, setSearch] = useState("");
  const [payFilter, setPayFilter] = useState("all"); // all | pending | paid | overdue
  const [clientFilter, setClientFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [nextNumberPreview, setNextNumberPreview] = useState("");
  const [remittanceOpen, setRemittanceOpen] = useState(false);
  const [remittanceFile, setRemittanceFile] = useState(null);
  const [remittanceLoading, setRemittanceLoading] = useState(false);
  const [remittanceResult, setRemittanceResult] = useState(null);
  const [certOpen, setCertOpen] = useState(false);
  const [certStatus, setCertStatus] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [certUploading, setCertUploading] = useState(false);
  const [certPassword, setCertPassword] = useState("");
  const [faceTarget, setFaceTarget] = useState(null);
  const clientOf = (inv) =>
    clients.find((c) => c.id === inv.client_id) || inv.client_snapshot || {};
  const onFaceUpdated = (id, face) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, face } : x)));
    setFaceTarget((t) => (t && t.id === id ? { ...t, face } : t));
  };
  const [availableCourses, setAvailableCourses] = useState([]);

  const [form, setForm] = useState({
    client_id: "",
    course_id: "",
    number: "",
    date: todayIso(),
    due_date: addDays(todayIso(), 90),
    lines: [emptyLine()],
    footer: {
      training_company: "",
      training_date: "",
      training_schedule: "",
      training_hours: "",
      training_students: "",
      payment_method: "MEDIANTE TRANSFERENCIA BANCARIA",
      bank_name: "",
      iban: "",
    },
    notes: "",
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, cliRes, prdRes, nnRes] = await Promise.all([
        adminApi.get("/admin/invoicing/invoices"),
        adminApi.get("/admin/invoicing/clients"),
        adminApi.get("/admin/invoicing/products"),
        adminApi.get("/admin/invoicing/invoices/counters/next-number"),
      ]);
      setItems(invRes.data || []);
      setClients(cliRes.data || []);
      setProducts(prdRes.data || []);
      setNextNumberPreview(nnRes.data?.next_number || "");
    } catch (e) {
      logError("No se pudieron cargar las facturas", e);
      toast.error("No se pudieron cargar las facturas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchAvailableCourses = useCallback(
    async (includeInvoiceId = null, clientIdFilter = null) => {
      try {
        const params = {};
        if (includeInvoiceId) params.invoice_id = includeInvoiceId;
        if (clientIdFilter) params.client_id = clientIdFilter;
        const res = await adminApi.get(
          "/admin/invoicing/available-courses",
          { params },
        );
        setAvailableCourses(res.data || []);
      } catch (e) {
        logError("No se pudieron cargar los cursos disponibles", e);
        setAvailableCourses([]);
      }
    },
    [],
  );

  useEffect(() => {
    if (!dialogOpen) return;
    fetchAvailableCourses(editingId, form.client_id || null);
  }, [dialogOpen, form.client_id, editingId, fetchAvailableCourses]);

  useEffect(() => {
    if (!dialogOpen || !form.course_id) return;
    const stillAvailable = availableCourses.some((c) => c.id === form.course_id);
    if (!stillAvailable) {
      setForm((prev) => ({ ...prev, course_id: "" }));
    }
  }, [dialogOpen, availableCourses, form.course_id]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    for (const l of form.lines) {
      const base = (parseInt(l.units, 10) || 0) * Number(l.unit_price || 0);
      subtotal += base;
      vat += base * (Number(l.vat_rate || 0) / 100);
    }
    return { subtotal, vat, total: subtotal + vat };
  }, [form.lines]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const today = todayIso();
    const payday = nextBsafePayday();
    return items.filter((it) => {
      if (payFilter === "paid" && !it.payment_date) return false;
      if (payFilter === "pending" && it.payment_date) return false;
      if (payFilter === "overdue") {
        if (it.payment_date) return false;
        if (!it.due_date || it.due_date >= today) return false;
      }
      if (payFilter === "bsafe" && !isBsafeExpected(it, payday)) return false;
      if (clientFilter !== "all" && it.client_id !== clientFilter) return false;
      const month = (it.date || "").slice(5, 7);
      if (monthFilter !== "all" && month !== monthFilter) return false;
      if (!q) return true;
      const hay = [
        it.number,
        it.client_snapshot?.name,
        it.client_snapshot?.nif,
        it.footer?.training_company,
        it.total ? String(it.total) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, payFilter, clientFilter, monthFilter]);

  const filteredTotal = useMemo(
    () => filteredItems.reduce((acc, i) => acc + (Number(i.total) || 0), 0),
    [filteredItems],
  );

  const activeFilters = [
    payFilter !== "all",
    clientFilter !== "all",
    monthFilter !== "all",
    !!search.trim(),
  ].filter(Boolean).length;

  const pendingTotal = useMemo(
    () =>
      items
        .filter((i) => !i.payment_date)
        .reduce((acc, i) => acc + (Number(i.total) || 0), 0),
    [items],
  );

  // -------------------------------------------------------------------
  // Helpers de formato de cursos
  // -------------------------------------------------------------------
  const _humanDate = (iso) => {
    if (!iso || iso.length < 10) return "";
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  };

  const _humanTime = (t) => (t ? `${t}H` : "");

  const _courseSchedule = (c) => {
    const s1 = _humanTime(c.time_start_day1);
    const e1 = _humanTime(c.time_end_day1);
    const s2 = _humanTime(c.time_start_day2);
    const e2 = _humanTime(c.time_end_day2);
    const s3 = _humanTime(c.time_start_day3);
    const e3 = _humanTime(c.time_end_day3);
    let out = "";
    if (s1 && e1) out = `${s1} a ${e1}`;
    if (s2 && e2) out += (out ? " y " : "") + `${s2} a ${e2}`;
    if (Number(c.days) >= 3 && s3 && e3) out += (out ? " y " : "") + `${s3} a ${e3}`;
    return out;
  };

  const _courseDates = (c) => {
    const parts = [
      _humanDate(c.date1),
      _humanDate(c.date2),
      Number(c.days) >= 3 ? _humanDate(c.date3) : "",
    ].filter(Boolean);
    if (parts.length <= 1) return parts[0] || "";
    return parts.slice(0, -1).join(", ") + ` y ${parts[parts.length - 1]}`;
  };

  const _courseHours = (c) => (c.hours ? String(c.hours) : "");

  const applyCourseToForm = (courseId) => {
    if (!courseId) {
      setForm((prev) => ({ ...prev, course_id: "" }));
      return;
    }
    const c = availableCourses.find((x) => x.id === courseId);
    setForm((prev) => ({
      ...prev,
      course_id: courseId,
      footer: {
        ...prev.footer,
        training_company: c?.client_name || prev.footer.training_company,
        training_date: _courseDates(c) || prev.footer.training_date,
        training_schedule: _courseSchedule(c) || prev.footer.training_schedule,
        training_hours: _courseHours(c) || prev.footer.training_hours,
        training_students:
          (c?.max_students ? String(c.max_students) : "") ||
          prev.footer.training_students,
      },
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      client_id: "",
      course_id: "",
      number: nextNumberPreview || "",
      date: todayIso(),
      due_date: addDays(todayIso(), 90),
      lines: [emptyLine()],
      footer: {
        training_company: "",
        training_date: "",
        training_schedule: "",
        training_hours: "",
        training_students: "",
        payment_method: "MEDIANTE TRANSFERENCIA BANCARIA",
        bank_name: "",
        iban: "",
      },
      notes: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setForm({
      client_id: inv.client_id || "",
      course_id: inv.course_id || "",
      number: inv.number || "",
      date: inv.date || todayIso(),
      due_date: inv.due_date || "",
      lines:
        inv.lines && inv.lines.length
          ? inv.lines.map((l) => ({
              description: l.description || "",
              units: l.units ?? 1,
              unit_price: l.unit_price ?? 0,
              vat_rate: l.vat_rate ?? 0,
            }))
          : [emptyLine()],
      footer: {
        training_company: inv.footer?.training_company || "",
        training_date: inv.footer?.training_date || "",
        training_schedule: inv.footer?.training_schedule || "",
        training_hours: inv.footer?.training_hours || "",
        training_students: inv.footer?.training_students || "",
        payment_method:
          inv.footer?.payment_method || "MEDIANTE TRANSFERENCIA BANCARIA",
        bank_name: inv.footer?.bank_name || "",
        iban: inv.footer?.iban || "",
      },
      notes: inv.notes || "",
    });
    setDialogOpen(true);
  };

  const openDuplicate = (inv) => {
    setDuplicateTarget(inv);
    setDuplicateNumber(nextNumberPreview || "");
    setDuplicateError("");
  };

  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    if (!/^\d{4}[-/]\d{1,6}$/.test(duplicateNumber.trim())) {
      setDuplicateError("El nº de factura debe ser AAAA-NNNN (ej. 2026-0113).");
      toast.error("El nº de factura debe ser AAAA-NNNN (ej. 2026-0113).");
      return;
    }
    setDuplicateError("");
    setDuplicating(true);
    try {
      await adminApi.post("/admin/invoicing/invoices", {
        client_id: duplicateTarget.client_id,
        course_id: null,
        requested_number: duplicateNumber.trim(),
        date: todayIso(),
        due_date: addDays(todayIso(), 90),
        lines: (duplicateTarget.lines || []).map((l) => ({
          description: l.description || "",
          units: Math.max(1, parseInt(l.units, 10) || 1),
          unit_price: Number(l.unit_price) || 0,
          vat_rate: Number(l.vat_rate) || 0,
          irpf_rate: 0,
        })),
        footer: duplicateTarget.footer || {},
        notes: duplicateTarget.notes || "",
      });
      toast.success(`Factura ${duplicateNumber.trim()} creada.`);
      setDuplicateTarget(null);
      fetchAll();
    } catch (e) {
      logError("Error duplicando factura", e);
      const msg = e?.response?.data?.detail || "No se pudo duplicar.";
      setDuplicateError(typeof msg === "string" ? msg : "No se pudo duplicar.");
      toast.error(typeof msg === "string" ? msg : "No se pudo duplicar.");
    } finally {
      setDuplicating(false);
    }
  };

  const handleLineChange = (idx, key, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [key]: value };
      return { ...prev, lines };
    });
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  };

  const removeLine = (idx) => {
    setForm((prev) => {
      const lines = prev.lines.filter((_, i) => i !== idx);
      return { ...prev, lines: lines.length ? lines : [emptyLine()] };
    });
  };

  const insertProduct = (idx, product) => {
    if (!product) return;
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = {
        description:
          product.name + (product.description ? ` — ${product.description}` : ""),
        units: 1,
        unit_price: product.unit_price ?? 0,
        vat_rate: product.vat_rate ?? 0,
      };
      return { ...prev, lines };
    });
  };

  const handleSave = async () => {
    if (!form.client_id) {
      toast.error("Selecciona un cliente.");
      return;
    }
    if (!form.date) {
      toast.error("Indica la fecha de la factura.");
      return;
    }
    if (!/^\d{4}[-/]\d{1,6}$/.test((form.number || "").trim())) {
      toast.error("El nº de factura debe ser AAAA-NNNN (ej. 2026-0113).");
      return;
    }
    if (!form.lines.length || !form.lines.some((l) => (l.description || "").trim())) {
      toast.error("Añade al menos una línea con descripción.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        course_id: form.course_id || null,
        requested_number: form.number.trim(),
        date: form.date,
        due_date: form.due_date || "",
        lines: form.lines
          .filter((l) => (l.description || "").trim())
          .map((l) => ({
            description: l.description.trim(),
            units: Math.max(1, parseInt(l.units, 10) || 1),
            unit_price: Number(l.unit_price) || 0,
            vat_rate: Number(l.vat_rate) || 0,
            irpf_rate: 0,
          })),
        footer: form.footer,
        notes: form.notes || "",
      };
      if (editingId) {
        await adminApi.put(`/admin/invoicing/invoices/${editingId}`, payload);
        toast.success("Factura actualizada.");
      } else {
        await adminApi.post(`/admin/invoicing/invoices`, payload);
        toast.success("Factura creada.");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      logError("Error guardando factura", e);
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admin/invoicing/invoices/${deleteTarget.id}`);
      toast.success("Factura eliminada.");
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  const openMarkPaid = (inv) => {
    setPayTarget(inv);
    setPayDate(todayIso());
  };

  const handleMarkPaid = async () => {
    if (!payTarget) return;
    if (!payDate) {
      toast.error("Indica la fecha del pago.");
      return;
    }
    setPaying(true);
    try {
      await adminApi.post(
        `/admin/invoicing/invoices/${payTarget.id}/mark-paid`,
        { payment_date: payDate },
      );
      toast.success(`Factura marcada como pagada (${fmtDate(payDate)}).`);
      setPayTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar como pagada.");
    } finally {
      setPaying(false);
    }
  };

  const handleRemittanceCheck = async () => {
    if (!remittanceFile) return;
    setRemittanceLoading(true);
    setRemittanceResult(null);
    try {
      const fd = new FormData();
      fd.append("file", remittanceFile);
      const res = await adminApi.post("/admin/invoicing/remittance/check", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setRemittanceResult(res.data);
      if (res.data.marked_paid > 0) {
        toast.success(
          `${res.data.marked_paid} factura(s) marcadas como pagadas.`
        );
      }
      if (res.data.mismatch_count > 0) {
        toast.warning(
          `${res.data.mismatch_count} factura(s) no cuadran. Revísalas abajo.`
        );
      }
      fetchAll();
    } catch (e) {
      logError("Error comprobando remesa", e);
      toast.error(
        e?.response?.data?.detail || "No se pudo leer la captura de la remesa."
      );
    } finally {
      setRemittanceLoading(false);
    }
  };

  const loadCertificate = async () => {
    try {
      const res = await adminApi.get("/admin/invoicing/certificate");
      setCertStatus(res.data);
    } catch (e) {
      logError("No se pudo leer el estado del certificado", e);
    }
  };

  const uploadCertificate = async () => {
    if (!certFile || !certPassword) return;
    setCertUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", certFile);
      fd.append("password", certPassword);
      await adminApi.post("/admin/invoicing/certificate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificado guardado. Ya puedes firmar e-facturas.");
      setCertFile(null);
      setCertPassword("");
      loadCertificate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el certificado.");
    } finally {
      setCertUploading(false);
    }
  };

  const handleUnmarkPaid = async (inv) => {
    try {
      await adminApi.post(`/admin/invoicing/invoices/${inv.id}/unmark-paid`);
      toast.success("Marcado de pago revertido.");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desmarcar.");
    }
  };

  return (
    <div data-testid="invoicing-invoices-section">
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">Facturas</h3>
          <p className="text-sm text-zinc-500">
            Pendiente de cobro:{" "}
            <b className="text-zinc-800 tabular-nums">{fmtEur(pendingTotal)}</b>
            {nextNumberPreview && (
              <span className="ml-2 text-xs text-zinc-400">
                Siguiente número: <b>{nextNumberPreview}</b>
              </span>
            )}
          </p>
          <p className="text-sm text-zinc-500" data-testid="invoicing-filtered-total">
            Suma de lo que estás viendo:{" "}
            <b className="text-zinc-900 tabular-nums">{fmtEur(filteredTotal)}</b>
            <span className="ml-2 text-xs text-zinc-400">
              {filteredItems.length} factura{filteredItems.length === 1 ? "" : "s"}
            </span>
          </p>
          {payFilter === "bsafe" && (
            <p className="text-sm text-indigo-900" data-testid="invoicing-bsafe-forecast">
              Previsto para cobrar de BSAFE el{" "}
              <b>{fmtDate(nextBsafePayday())}</b>:{" "}
              <b className="tabular-nums">{fmtEur(filteredTotal)}</b>
              {(() => {
                const late = filteredItems.filter(isOverdue).length;
                return late ? (
                  <span className="ml-2 text-xs text-red-700">
                    ({late} ya vencida{late === 1 ? "" : "s"})
                  </span>
                ) : null;
              })()}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="h-10 border-zinc-300"
            onClick={() => {
              setCertOpen(true);
              loadCertificate();
            }}
            data-testid="invoicing-certificate-open-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            Certificado
          </Button>
          <Button
            variant="outline"
            className="h-10 border-zinc-300"
            onClick={() => {
              setRemittanceOpen(true);
              setRemittanceResult(null);
              setRemittanceFile(null);
            }}
            data-testid="invoicing-remittance-open-btn"
          >
            <ScanLine className="h-4 w-4 mr-2" />
            Comprobar remesa BSAFE
          </Button>
          <Button
            className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white"
            onClick={openCreate}
            data-testid="invoicing-invoices-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva factura
          </Button>
        </div>
      </div>

      {/* Filtros al estilo de los cursos: estado, cliente y mes */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-1">
          {[
            ["all", "TODAS"],
            ["pending", "PENDIENTES"],
            ["paid", "PAGADAS"],
            ["overdue", "VENCIDAS"],
            ["bsafe", "BSAFE"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setPayFilter(value)}
              className={`h-8 px-3 rounded-sm text-xs font-bold tracking-wide transition-colors ${
                payFilter === value
                  ? "bg-zinc-950 text-white"
                  : "text-zinc-600 hover:text-zinc-900"
              }`}
              data-testid={`invoicing-filter-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger
            className="h-10 w-56"
            data-testid="invoicing-filter-client"
          >
            <SelectValue placeholder="CLIENTE" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">CLIENTE: TODOS</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="h-10 w-40" data-testid="invoicing-filter-month">
            <SelectValue placeholder="MES" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">MES: TODOS</SelectItem>
            {MONTHS_ES.map((label, idx) => (
              <SelectItem key={label} value={String(idx + 1).padStart(2, "0")}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeFilters > 0 && (
          <Button
            variant="ghost"
            className="h-10 text-zinc-600"
            onClick={() => {
              setPayFilter("all");
              setClientFilter("all");
              setMonthFilter("all");
              setSearch("");
            }}
            data-testid="invoicing-filters-clear"
          >
            <X className="h-4 w-4 mr-1" />
            Limpiar
          </Button>
        )}
        <Input
          placeholder="Buscar por número, cliente, NIF…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 w-64"
          data-testid="invoicing-invoices-search"
        />
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-10">Cargando…</div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 border-2 border-dashed border-zinc-300 rounded-md">
          <FileText className="h-12 w-12 mx-auto mb-3 text-zinc-400" />
          <p className="font-semibold text-zinc-700">
            {items.length === 0 ? "Aún no hay facturas" : "Sin resultados"}
          </p>
          <p className="text-sm mt-2 max-w-md mx-auto">
            {items.length === 0
              ? 'Pulsa "Nueva factura" para crear la primera.'
              : "Prueba con otros filtros o términos de búsqueda."}
          </p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-auto max-h-[68vh]">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-zinc-100 text-zinc-700 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Nº</th>
                <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                <th className="text-right px-3 py-2 font-semibold">Total</th>
                <th className="text-center px-3 py-2 font-semibold">Cobro</th>
                <th className="text-center px-3 py-2 font-semibold w-[330px]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((inv) => {
                const overdue = isOverdue(inv);
                return (
                  <tr
                    key={inv.id}
                    className={`border-t border-zinc-200 ${
                      overdue ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"
                    }`}
                    data-testid={`invoicing-invoice-row-${inv.id}`}
                  >
                    <td
                      className={`px-3 py-2 font-mono font-semibold whitespace-nowrap ${
                        overdue ? "text-red-800" : "text-zinc-900"
                      }`}
                    >
                      {inv.number || "—"}
                    </td>
                    <td className={`px-3 py-2 ${overdue ? "text-red-800" : "text-zinc-700"}`}>
                      {fmtDate(inv.date)}
                      {inv.due_date && (
                        <div className="text-[11px] text-zinc-500">
                          Vence {fmtDate(inv.due_date)}
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2 ${overdue ? "text-red-900" : "text-zinc-900"}`}>
                      <div className="font-semibold">
                        {inv.client_snapshot?.name || "—"}
                      </div>
                      {isIssefeClient(inv.client_snapshot) &&
                        !!inv.footer?.training_company && (
                          <div
                            className="text-[11px] text-zinc-500 uppercase tracking-wide"
                            data-testid={`invoicing-invoice-training-company-${inv.id}`}
                          >
                            {inv.footer.training_company}
                          </div>
                        )}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap ${
                        overdue ? "text-red-900" : "text-zinc-900"
                      }`}
                    >
                      {fmtEur(inv.total)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {inv.payment_date ? (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300"
                          title="Fecha de cobro"
                          data-testid={`invoicing-invoice-paid-badge-${inv.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          {fmtDate(inv.payment_date)}
                        </span>
                      ) : (
                        <span
                          className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                            overdue
                              ? "bg-red-600 text-white border-red-700"
                              : "bg-amber-50 text-amber-800 border-amber-300"
                          }`}
                          title={overdue ? "Pendiente y vencida" : "Pendiente de cobro"}
                          data-testid={`invoicing-invoice-pending-badge-${inv.id}`}
                        >
                          PENDIENTE
                        </span>
                      )}
                      {inv.face?.numero_registro && (
                        <div
                          className="mt-1 text-[10px] font-bold uppercase tracking-wide text-indigo-800"
                          title={`FACe · registro ${inv.face.numero_registro}`}
                          data-testid={`invoicing-invoice-face-badge-${inv.id}`}
                        >
                          FACe · {inv.face.tramitacion?.name || "Enviada"}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadInvoicePdf(inv)}
                          data-testid={`invoicing-invoice-pdf-${inv.id}`}
                          title="Descargar PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadFacturae(inv)}
                          data-testid={`invoicing-invoice-efactura-${inv.id}`}
                          title="Generar e-factura firmada (Facturae para FACe)"
                        >
                          <FileCode2 className="h-4 w-4" />
                        </Button>
                        {(clientOf(inv).is_public_admin || inv.face?.numero_registro) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setFaceTarget(inv)}
                            data-testid={`invoicing-invoice-face-${inv.id}`}
                            title="Enviar a FACe / ver estado"
                            className="text-indigo-800 hover:bg-indigo-50"
                          >
                            <Landmark className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDuplicate(inv)}
                          data-testid={`invoicing-invoice-duplicate-${inv.id}`}
                          title="Duplicar factura"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(inv)}
                          data-testid={`invoicing-invoice-edit-${inv.id}`}
                          title="Editar factura"
                          className="border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white h-8 px-2"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs font-semibold">EDITAR</span>
                        </Button>
                        {!inv.payment_date ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMarkPaid(inv)}
                            data-testid={`invoicing-invoice-mark-paid-${inv.id}`}
                            title="Marcar como pagada"
                            className="border-green-700 text-green-800 hover:bg-green-700 hover:text-white h-8 px-2"
                          >
                            <CircleDollarSign className="h-3.5 w-3.5 mr-1" />
                            <span className="text-xs font-semibold">PAGADA</span>
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnmarkPaid(inv)}
                            data-testid={`invoicing-invoice-unmark-paid-${inv.id}`}
                            title="Deshacer marcado de pago"
                            className="text-zinc-600 hover:bg-zinc-100"
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteTarget(inv)}
                          data-testid={`invoicing-invoice-delete-${inv.id}`}
                          title="Eliminar factura"
                          className="text-red-600 hover:bg-red-50 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de creación/edición */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="invoicing-invoice-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar factura" : "Nueva factura"}
            </DialogTitle>
            <DialogDescription>
              La factura se crea directamente con su número. Puedes editarla o
              eliminarla cuando quieras.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Curso vinculado (opcional) */}
            <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3">
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Curso vinculado{" "}
                <span className="text-zinc-400 font-normal">
                  (opcional — autocompleta empresa, fecha, horario y alumnos)
                </span>
              </label>
              <Select
                value={form.course_id || "__none__"}
                onValueChange={(v) =>
                  applyCourseToForm(v === "__none__" ? "" : v)
                }
              >
                <SelectTrigger
                  className="h-10"
                  data-testid="invoicing-invoice-course-select"
                >
                  <SelectValue placeholder="Selecciona curso…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">
                    — Sin vincular a curso —
                  </SelectItem>
                  {availableCourses.length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-500">
                      No hay cursos disponibles (todos tienen ya factura).
                    </div>
                  )}
                  {availableCourses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {(_humanDate(c.date1) || "?")}
                      {" · "}
                      {c.client_name || "?"}
                      {c.city ? ` · ${c.city}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-zinc-500">
                Solo aparecen cursos que aún no tienen factura. Elimina la
                anterior si necesitas volver a facturar un curso.
              </p>
            </div>

            {/* Cliente, número y fechas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Cliente *
                </label>
                <Select
                  value={form.client_id || undefined}
                  onValueChange={(v) => setForm({ ...form, client_id: v })}
                >
                  <SelectTrigger
                    className="h-10"
                    data-testid="invoicing-invoice-client-select"
                  >
                    <SelectValue placeholder="Selecciona cliente…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="inline-flex items-center gap-2">
                          {c.group === "ISSEFE" && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                              ISSEFE
                            </span>
                          )}
                          {c.group === "DESFIEMER" && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">
                              DESFIEMER
                            </span>
                          )}
                          {c.name}
                          {c.nif ? ` — ${c.nif}` : ""}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Nº de factura *
                </label>
                <Input
                  value={form.number}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  placeholder={nextNumberPreview || "2026-0113"}
                  className="h-10 font-mono"
                  data-testid="invoicing-invoice-number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Fecha *
                </label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      date: e.target.value,
                      due_date: addDays(e.target.value, 90),
                    })
                  }
                  className="h-10"
                  data-testid="invoicing-invoice-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Vencimiento
                </label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="h-10"
                  data-testid="invoicing-invoice-due-date"
                />
              </div>
            </div>

            {/* Líneas */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-zinc-700">
                  Conceptos
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addLine}
                  data-testid="invoicing-invoice-add-line"
                >
                  <Plus className="h-3 w-3 mr-1" /> Añadir línea
                </Button>
              </div>
              <div className="border border-zinc-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-600 text-xs">
                    <tr>
                      <th className="text-left px-2 py-1 font-semibold w-1/2">Concepto</th>
                      <th className="text-right px-2 py-1 font-semibold w-16">Uds.</th>
                      <th className="text-right px-2 py-1 font-semibold w-28">Base ud.</th>
                      <th className="text-right px-2 py-1 font-semibold w-16">% IVA</th>
                      <th className="text-right px-2 py-1 font-semibold w-24">Total</th>
                      <th className="text-center px-2 py-1 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, idx) => {
                      const base = (parseInt(l.units, 10) || 0) * Number(l.unit_price || 0);
                      const total = base * (1 + Number(l.vat_rate || 0) / 100);
                      return (
                        <tr key={idx} className="border-t border-zinc-100 align-top">
                          <td className="px-2 py-1">
                            <Textarea
                              rows={1}
                              value={l.description}
                              onChange={(e) =>
                                handleLineChange(idx, "description", e.target.value)
                              }
                              className="min-h-[36px] text-sm"
                              placeholder="Descripción del concepto…"
                              data-testid={`invoicing-line-desc-${idx}`}
                            />
                            {products.length > 0 && (
                              <Select
                                value=""
                                onValueChange={(v) =>
                                  insertProduct(idx, products.find((p) => p.id === v))
                                }
                              >
                                <SelectTrigger
                                  className="h-7 mt-1 text-xs text-zinc-500 border-dashed"
                                  data-testid={`invoicing-line-product-${idx}`}
                                >
                                  <SelectValue placeholder="Insertar del catálogo…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.name} — {fmtEur(p.unit_price)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="1"
                              min="1"
                              value={l.units}
                              onChange={(e) =>
                                handleLineChange(
                                  idx,
                                  "units",
                                  e.target.value.replace(/[^0-9]/g, "")
                                )
                              }
                              onBlur={(e) =>
                                handleLineChange(
                                  idx,
                                  "units",
                                  String(Math.max(1, parseInt(e.target.value, 10) || 1))
                                )
                              }
                              className="h-9 text-right"
                              data-testid={`invoicing-line-units-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={l.unit_price}
                              onChange={(e) =>
                                handleLineChange(idx, "unit_price", e.target.value)
                              }
                              className="h-9 text-right"
                              data-testid={`invoicing-line-price-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              max="100"
                              value={l.vat_rate}
                              onChange={(e) =>
                                handleLineChange(idx, "vat_rate", e.target.value)
                              }
                              className="h-9 text-right"
                              data-testid={`invoicing-line-vat-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-semibold tabular-nums pt-3">
                            {fmtEur(total)}
                          </td>
                          <td className="px-1 py-1 text-center pt-3">
                            <button
                              type="button"
                              onClick={() => removeLine(idx)}
                              className="text-red-600 hover:text-red-800"
                              data-testid={`invoicing-line-remove-${idx}`}
                              title="Quitar línea"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-zinc-50 text-sm">
                    <tr className="border-t border-zinc-200">
                      <td colSpan={4} className="px-2 py-1 text-right text-zinc-600">
                        Base imponible
                      </td>
                      <td className="px-2 py-1 text-right font-semibold tabular-nums">
                        {fmtEur(totals.subtotal)}
                      </td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="px-2 py-1 text-right text-zinc-600">
                        Total IVA
                      </td>
                      <td className="px-2 py-1 text-right font-semibold tabular-nums">
                        {fmtEur(totals.vat)}
                      </td>
                      <td></td>
                    </tr>
                    <tr className="border-t border-zinc-200">
                      <td colSpan={4} className="px-2 py-1 text-right font-bold text-zinc-900">
                        TOTAL
                      </td>
                      <td className="px-2 py-1 text-right font-bold text-lg tabular-nums text-zinc-900">
                        {fmtEur(totals.total)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Datos de la formación */}
            <div>
              <p className="text-sm font-medium text-zinc-700 mb-1">
                Datos del curso (opcional, se imprime al pie)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  placeholder="Empresa de impartición"
                  value={form.footer.training_company}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      footer: { ...form.footer, training_company: e.target.value },
                    })
                  }
                  className="h-10"
                  data-testid="invoicing-invoice-footer-company"
                />
                <Input
                  placeholder="Fecha de la formación (ej. 24/04/2026)"
                  value={form.footer.training_date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      footer: { ...form.footer, training_date: e.target.value },
                    })
                  }
                  className="h-10"
                  data-testid="invoicing-invoice-footer-date"
                />
                <Input
                  placeholder="Horario (ej. 9:00H a 15:00H)"
                  value={form.footer.training_schedule}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      footer: { ...form.footer, training_schedule: e.target.value },
                    })
                  }
                  className="h-10"
                  data-testid="invoicing-invoice-footer-schedule"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Horas (ej. 6+2)"
                    value={form.footer.training_hours}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        footer: { ...form.footer, training_hours: e.target.value },
                      })
                    }
                    className="h-10"
                    data-testid="invoicing-invoice-footer-hours"
                  />
                  <Input
                    placeholder="Nº alumnos"
                    value={form.footer.training_students}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        footer: { ...form.footer, training_students: e.target.value },
                      })
                    }
                    className="h-10"
                    data-testid="invoicing-invoice-footer-students"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              data-testid="invoicing-invoice-dialog-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-invoice-dialog-save"
            >
              {saving
                ? "Guardando…"
                : editingId
                ? "Guardar cambios"
                : "Crear factura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicar — pide el número de la nueva factura */}
      <Dialog
        open={!!duplicateTarget}
        onOpenChange={(o) => !o && !duplicating && setDuplicateTarget(null)}
      >
        <DialogContent className="max-w-md" data-testid="invoicing-invoice-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Duplicar factura {duplicateTarget?.number}</DialogTitle>
            <DialogDescription>
              Se creará una factura nueva con los mismos conceptos y cliente.
              Indica el número que le corresponde.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Nº de la nueva factura *
            </label>
            <Input
              value={duplicateNumber}
              onChange={(e) => setDuplicateNumber(e.target.value)}
              placeholder={nextNumberPreview || "2026-0113"}
              className="h-10 font-mono"
              autoFocus
              data-testid="invoicing-invoice-duplicate-number"
            />
            {duplicateError && (
              <p
                className="mt-2 text-sm font-semibold text-red-600"
                data-testid="invoicing-invoice-duplicate-error"
              >
                {duplicateError}
              </p>
            )}
            <p className="mt-2 text-xs text-zinc-500">
              Importe: <b>{fmtEur(duplicateTarget?.total || 0)}</b> ·{" "}
              {duplicateTarget?.client_snapshot?.name}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDuplicateTarget(null)}
              disabled={duplicating}
              data-testid="invoicing-invoice-duplicate-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={duplicating}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-invoice-duplicate-confirm"
            >
              {duplicating ? "Creando…" : "Crear factura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Marcar como pagada */}
      <Dialog
        open={!!payTarget}
        onOpenChange={(o) => !o && !paying && setPayTarget(null)}
      >
        <DialogContent
          className="max-w-md"
          data-testid="invoicing-invoice-mark-paid-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5 text-green-700" />
              Marcar factura {payTarget?.number} como pagada
            </DialogTitle>
            <DialogDescription>
              Introduce la fecha en la que el cliente realizó el pago.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Fecha del pago *
              </label>
              <Input
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                className="h-10"
                autoFocus
                data-testid="invoicing-invoice-mark-paid-date"
              />
            </div>
            <div className="text-xs text-zinc-500">
              Cliente:{" "}
              <span className="font-medium text-zinc-800">
                {payTarget?.client_snapshot?.name}
              </span>
              {" · "}Importe:{" "}
              <span className="font-medium text-zinc-800">
                {fmtEur(payTarget?.total || 0)}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPayTarget(null)}
              disabled={paying}
              data-testid="invoicing-invoice-mark-paid-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={paying || !payDate}
              className="bg-green-700 hover:bg-green-800 text-white"
              data-testid="invoicing-invoice-mark-paid-confirm"
            >
              {paying ? "Guardando…" : "Marcar como pagada"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificado digital para firmar las e-facturas */}
      <FaceDialog
        key={faceTarget?.id || "none"}
        invoice={faceTarget}
        client={faceTarget ? clientOf(faceTarget) : null}
        onClose={() => setFaceTarget(null)}
        onUpdated={onFaceUpdated}
      />
      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent data-testid="invoicing-certificate-dialog">
          <DialogHeader>
            <DialogTitle>Certificado digital</DialogTitle>
            <DialogDescription>
              Con él se firman las e-facturas (Facturae 3.2.2) que se suben a
              FACe. Sube tu fichero .p12 o .pfx y escribe su contraseña; se
              guarda cifrada en el servidor.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-sm">
              {certStatus?.uploaded ? (
                <div data-testid="invoicing-certificate-info">
                  <p className="font-semibold text-emerald-800">
                    Certificado cargado
                  </p>
                  <p className="text-zinc-700 mt-1">
                    {certStatus.info?.subject || certStatus.filename}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Válido hasta {certStatus.info?.valid_until || "—"}
                  </p>
                </div>
              ) : (
                <p className="text-zinc-700" data-testid="invoicing-certificate-empty">
                  Todavía no hay certificado. Sube tu fichero .p12 para poder
                  firmar.
                </p>
              )}
            </div>

            <Input
              key={certStatus?.uploaded_at || "empty"}
              type="file"
              accept=".p12,.pfx"
              onChange={(e) => setCertFile(e.target.files?.[0] || null)}
              className="h-11"
              data-testid="invoicing-certificate-file-input"
            />
            <Input
              type="password"
              value={certPassword}
              onChange={(e) => setCertPassword(e.target.value)}
              placeholder="Contraseña del certificado"
              autoComplete="new-password"
              className="h-11"
              data-testid="invoicing-certificate-password-input"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCertOpen(false)}
              data-testid="invoicing-certificate-close-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={uploadCertificate}
              disabled={!certFile || !certPassword || certUploading}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-certificate-upload-btn"
            >
              {certUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Guardar certificado
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remesas BSAFE: se lee la captura y se cruzan los importes */}
      <Dialog open={remittanceOpen} onOpenChange={setRemittanceOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="invoicing-remittance-dialog"
        >
          <DialogHeader>
            <DialogTitle>Comprobar remesa de BSAFE</DialogTitle>
            <DialogDescription>
              Sube la captura del listado de pagos. Se leen los números de
              factura y sus importes, se marcan como pagadas las que cuadran y
              se avisa de las que no.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => {
                setRemittanceFile(e.target.files?.[0] || null);
                setRemittanceResult(null);
              }}
              className="h-11"
              data-testid="invoicing-remittance-file-input"
            />

            {remittanceResult && (
              <div className="space-y-3" data-testid="invoicing-remittance-results">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span>
                    Leídas: <b>{remittanceResult.rows_read}</b>
                  </span>
                  <span>
                    Total remesa:{" "}
                    <b className="tabular-nums">
                      {fmtEur(remittanceResult.remittance_total)}
                    </b>
                  </span>
                  <span className="text-green-800">
                    Marcadas pagadas: <b>{remittanceResult.marked_paid}</b>
                  </span>
                  {remittanceResult.mismatch_count > 0 && (
                    <span className="text-red-700 font-semibold">
                      No cuadran: {remittanceResult.mismatch_count}
                    </span>
                  )}
                </div>

                {remittanceResult.mismatch_count > 0 && (
                  <div
                    className="flex gap-2 items-start border border-red-300 bg-red-50 text-red-900 rounded-md p-3 text-sm"
                    data-testid="invoicing-remittance-alert"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      Hay facturas de la remesa que no coinciden con las de la
                      app. No se han marcado como pagadas; revísalas una a una.
                    </span>
                  </div>
                )}

                <div className="border border-zinc-200 rounded-md overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100 text-zinc-700">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Nº</th>
                        <th className="text-right px-3 py-2 font-semibold">Remesa</th>
                        <th className="text-right px-3 py-2 font-semibold">Factura</th>
                        <th className="text-left px-3 py-2 font-semibold">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {remittanceResult.results.map((r, i) => {
                        const bad =
                          r.status === "importe_distinto" ||
                          r.status === "no_encontrada";
                        return (
                          <tr
                            key={`${r.number}-${i}`}
                            className={`border-t border-zinc-200 ${
                              bad ? "bg-red-50" : ""
                            }`}
                            data-testid={`invoicing-remittance-row-${i}`}
                          >
                            <td className="px-3 py-2 font-mono">
                              {r.invoice_number || r.number}
                              {!!r.training_company && (
                                <div className="text-[11px] text-zinc-500 uppercase">
                                  {r.training_company}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {fmtEur(r.amount)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {r.invoice_total != null ? fmtEur(r.invoice_total) : "—"}
                            </td>
                            <td
                              className={`px-3 py-2 ${
                                bad ? "text-red-800 font-semibold" : "text-zinc-700"
                              }`}
                            >
                              {r.detail}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemittanceOpen(false)}
              data-testid="invoicing-remittance-close-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={handleRemittanceCheck}
              disabled={!remittanceFile || remittanceLoading}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="invoicing-remittance-check-btn"
            >
              {remittanceLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Leyendo captura…
                </>
              ) : (
                <>
                  <ScanLine className="h-4 w-4 mr-2" />
                  Comprobar y marcar pagadas
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="invoicing-invoice-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Eliminar factura {deleteTarget?.number}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="invoicing-invoice-delete-confirm"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
